import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box,
    Button, TextField, MenuItem, FormControl, InputLabel, Select, RadioGroup, FormControlLabel, Radio, Grid
} from '@mui/material';
import { Note, CreateNoteRequest, NoteTypeDto, getNoteTypeIdByCode } from '../../models/Note';
import TagInput from './TagInput';
import { useReferenceData } from '../../components/ReferenceDataContext';
interface NoteFormProps {
    open: boolean;
    note: Note | null;
    onSubmit: (noteData: CreateNoteRequest) => void;
    onCancel: () => void;
    loading?: boolean;
    availableTags?: string[];
}

const NoteForm: React.FC<NoteFormProps> = ({ open, note, onSubmit, onCancel, loading = false, availableTags = [] }) => {
    const [noteTypesRef, setNoteTypesRef] = useState<{ value: number; label: string }[]>([]);
    const { noteTypes, noteTypesState } = useReferenceData();
    const [defaultNoteTypeId, setDefaultNoteTypeId] = useState<number>(1);

    useEffect(() => {

        let mounted = true;

        (async () => {

            if (!mounted) return;

            if (noteTypesState.loading) return;

            if (noteTypesState.error) {
                return <div>Error: {noteTypesState.error}</div>;
            }

            if (!noteTypesState.data || noteTypesState.data.length === 0) {
                return <div>No note types available</div>;
            }

            if (noteTypes) {
                const noteTypeId = getNoteTypeIdByCode(noteTypes, "GENERAL")
                setDefaultNoteTypeId(noteTypeId);

                const options = noteTypes
                    .sort((a, b) => a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.name.localeCompare(b.name))
                    .map(x => ({ value: x.noteTypeId, label: x.name }));

                setNoteTypesRef(options);
            }
        })();

        return () => { mounted = false; };
    }, []);

    const [formData, setFormData] = useState<CreateNoteRequest>({
        artworkId: 0,
        title: '',
        content: '',
        noteTypeId: defaultNoteTypeId,
        tags: [],
        priority: 0
    });

    useEffect(() => {
        if (note && note.id) {
            setFormData({
                artworkId: note.artworkId,
                title: note.title || '',
                content: note.content || '',
                noteTypeId: note.noteTypeId || defaultNoteTypeId,
                tags: note.tags || [],
                priority: note.priority || 0
            });
        } else {
            // Reset form for new note
            setFormData({
                artworkId: 0,
                title: '',
                content: '',
                noteTypeId: defaultNoteTypeId,
                tags: [],
                priority: 0
            });
        }
    }, [note]);

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!loading) {
            onSubmit(formData);
        }
    };

    const handleTagAdd = (tag: string): void => {
        if (!loading) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
        }
    };

    const handleTagRemove = (tagToRemove: string): void => {
        if (!loading) {
            setFormData(prev => ({
                ...prev,
                tags: prev.tags.filter(tag => tag !== tagToRemove)
            }));
        }
    };

    const handleUpdate = () => {
        if (!loading) {
            onSubmit(formData);
        }
    }

    return (
        <Dialog open={open} onClose={onCancel} maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%'
                }
            }}>
            <DialogTitle>{note?.id ? 'Edit Note' : 'Create New Note'}</DialogTitle>
            <hr></hr>
            <DialogContent>
                    <div className="form-group">
                        <label htmlFor="title">Title *</label>
                        <input
                            id="title"
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="type">Type</label>
                            <select
                                id="type"
                                value={formData.noteTypeId}
                                onChange={(e) => setFormData(prev => ({ ...prev, noteTypeId: Number(e.target.value) }))}
                                disabled={loading}
                            >
                                {noteTypesRef.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="content">Content *</label>
                        <textarea
                            id="content"
                            value={formData.content}
                            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                            rows={6}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label>Tags</label>
                        <TagInput
                            tags={formData.tags}
                            onTagAdd={handleTagAdd}
                            onTagRemove={handleTagRemove}
                            disabled={loading}
                            availableTags={availableTags} 
                        />
                    </div>
        
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" disabled={loading} onClick={handleUpdate}> {loading ? 'Saving...' : (note?.id ? 'Update Note' : 'Create Note')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoteForm;